import type { RoomResourceListItem } from "../hooks/useGetRoomResourceItemsQuery";
import type { AudioContent } from "../hooks/usePlayAudioContent";
import { getItemTranslation } from "../../app/utils/getItemTranslation";
import { formatArticleDate, formatShortDate } from "../../app/utils/renderDate";
import { z } from "zod";
import { thumbnailUrl } from "../../../../../api/src/shared/domain/video-url";
import {
  journalSource,
  podcastSource,
  type PublicationSource,
} from "../../../components/ui/SourceLabel";

/**
 * What opening a room's resource does, inside that room: a page, or the
 * player for a standalone audio file, which has no page. A Nectar has neither
 * on the web, so it is not offered, as on the mobile Resources tab.
 */
export type RoomResourceTarget =
  | { kind: "page"; path: string }
  | { kind: "audio"; content: AudioContent }
  | { kind: "none" };

export function roomResourceTarget(
  item: RoomResourceListItem,
  roomId: string
): RoomResourceTarget {
  switch (item.kind) {
    case "podcast":
      return {
        kind: "page",
        path: `/room/podcast/${roomId}/${item.podcast.id}`,
      };
    case "playlist":
      return {
        kind: "page",
        path: `/room/playlist/${roomId}/${item.playlist.id}`,
      };
    case "article":
      return {
        kind: "page",
        path: `/room/post/${roomId}/${item.article.slug}`,
      };
    case "content": {
      const { content } = item;
      switch (content.content_format) {
        case "Video":
          return {
            kind: "page",
            path: `/room/video/${roomId}/${content.slug}`,
          };
        case "Infographic":
          return {
            kind: "page",
            path: `/room/infographic/${roomId}/${content.slug}`,
          };
        case "Audio":
          return { kind: "audio", content };
        case "Nectar":
          return { kind: "none" };
        default:
          throw new Error(content satisfies never);
      }
    }
    default:
      throw new Error(item satisfies never);
  }
}

export function roomResourceTitle(item: RoomResourceListItem): string {
  switch (item.kind) {
    case "podcast":
      return item.podcast.title;
    case "playlist":
      return item.playlist.title;
    case "article":
      return item.article.title;
    case "content":
      return item.content.title;
    default:
      throw new Error(item satisfies never);
  }
}

/**
 * What the resource is, as its type tag reads: the article's publication type
 * or the media's format when it has one, otherwise the kind itself, which the
 * caller translates.
 */
export function roomResourceType(
  item: RoomResourceListItem
):
  | { kind: "label"; label: string }
  | { kind: "collection" | "podcast" | "article" } {
  switch (item.kind) {
    case "podcast":
      return { kind: "podcast" };
    case "playlist":
      return { kind: "collection" };
    case "article":
      return item.article.article_type === null
        ? { kind: "article" }
        : { kind: "label", label: item.article.article_type };
    case "content":
      return { kind: "label", label: item.content.content_format };
    default:
      throw new Error(item satisfies never);
  }
}

/**
 * The resource's picture for a list row: the media's thumbnail, the paper's
 * card image, the collection's cover. A podcast has none.
 */
export function roomResourceImageUrl(
  item: RoomResourceListItem
): string | null {
  switch (item.kind) {
    case "podcast":
      return null;
    case "playlist": {
      const cover = item.playlist.images.at(0);
      return cover === undefined ? null : cover.url;
    }
    case "article": {
      const { cardImage } = item.article;
      return cardImage === null
        ? null
        : cardImage.sizes === null
          ? cardImage.url
          : cardImage.sizes.medium.file;
    }
    case "content":
      return thumbnailUrl(item.content);
    default:
      throw new Error(item satisfies never);
  }
}

/**
 * What the resource is about, in a word (Figma "Ready for dev", the line under
 * a resource's title): its first tag, else its first specialty. Empty when it
 * has neither.
 */
export function roomResourceSubject(item: RoomResourceListItem): string {
  const subjects =
    item.kind === "article"
      ? item.article.medical_specialties
      : item.kind === "content"
        ? [...item.content.tags, ...item.content.medical_specialties]
        : item.kind === "playlist"
          ? [...item.playlist.tags, ...item.playlist.medical_specialties]
          : item.podcast.medical_specialties;
  const first = subjects.at(0);
  return first === undefined ? "" : getItemTranslation(first);
}

/**
 * The date on a resource's row. An article's publication day reads in UTC; a
 * content, a playlist, a podcast or an undated article (sorted by when it was
 * added) reads in the reader's zone.
 */
export function roomResourceDate(
  item: RoomResourceListItem,
  locale: string
): string {
  return item.kind === "article" && item.article.publication_date !== null
    ? formatArticleDate(item.article.publication_date, locale)
    : formatShortDate(new Date(item.sortDate), locale);
}

// Read at a boundary, not trusted from the type: after a rollback an older
// API sends no `episodeOf`, and a new bundle must read that as "not a
// stand-in" rather than crash on `episodeOf.owner`.
const standingIn = z.object({
  episodeOf: z.object({ owner: z.object({ name: z.string() }).nullable() }),
});

/**
 * The source line of an Audio content in "All": an episode that stands in for
 * its series (one entry per podcast) names the series' owner, as the series
 * card did, even when the episode has an owner of its own; any other Audio
 * content, and a body that has no `episodeOf` at all, names its journal.
 */
export function roomAudioSource(item: {
  content: { journal: { name: string } | null };
}): PublicationSource | null {
  const parsed = standingIn.safeParse(item);
  return parsed.success
    ? podcastSource(parsed.data.episodeOf.owner)
    : journalSource(item.content.journal);
}
